import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import {
  LayoutDashboard, Crosshair, Star, Bell, Building2,
  Users, Settings, Activity, Target, ChevronLeft,
  ChevronRight, LogOut, Shield, FileText, Award,
} from "lucide-react";

const NAV_ITEMS = [
  { key: "dashboard",    label: "¿Qué trabajar hoy?",  icon: LayoutDashboard, path: "/dashboard" },
  { key: "radar",        label: "Radar de Procesos",    icon: Crosshair,       path: "/radar" },
  { key: "favoritos",    label: "Mis Favoritos",        icon: Star,            path: "/favoritos" },
  { key: "alertas",      label: "Centro de Alertas",    icon: Bell,            path: "/alertas" },
  { key: "documentos",   label: "Documentos",           icon: FileText,        path: "/documentos" },
  { key: "experiencias", label: "Experiencias",         icon: Award,           path: "/experiencias" },
  { key: "health",       label: "Health Check",         icon: Activity,        path: "/health", adminOnly: true },
];

const ADMIN_ITEMS = [
  { key: "perfil", label: "Perfil Empresarial", icon: Building2, path: "/perfil" },
  { key: "usuarios", label: "Gestión Usuarios", icon: Users, path: "/admin/usuarios" },
  { key: "configuracion", label: "Config. SECOP", icon: Settings, path: "/admin/config" },
];

export default function Sidebar({ collapsed, onToggle, unreadAlerts = 0 }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      data-testid={TEST_IDS.SIDEBAR}
      className={`flex flex-col h-full bg-slate-900 border-r border-slate-700/50 transition-all duration-300 ${
        collapsed ? "w-16" : "w-60"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-slate-700/50">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500/15 border border-emerald-500/30 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-100 font-outfit font-bold text-sm leading-none">RadarLit</p>
              <p className="text-slate-500 text-[10px] leading-none mt-0.5">SECOP II</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 mx-auto bg-emerald-500/15 border border-emerald-500/30 rounded-lg flex items-center justify-center">
            <Target className="w-4 h-4 text-emerald-400" />
          </div>
        )}
        <button
          data-testid={TEST_IDS.SIDEBAR_TOGGLE}
          onClick={onToggle}
          className={`text-slate-500 hover:text-slate-200 transition-colors p-1 rounded-lg hover:bg-slate-800 ${collapsed ? "mx-auto mt-2" : ""}`}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
        {NAV_ITEMS.filter(item => !item.adminOnly || ["ADMIN","SUPER_ADMIN"].includes(user?.role)).map((item) => (
          <SidebarLink
            key={item.key}
            item={item}
            collapsed={collapsed}
            badge={item.key === "alertas" && unreadAlerts > 0 ? unreadAlerts : null}
          />
        ))}

        {["ADMIN", "SUPER_ADMIN"].includes(user?.role) && (
          <>
            <div className={`px-2 pt-3 pb-1 ${collapsed ? "hidden" : ""}`}>
              <p className="text-slate-600 text-[10px] uppercase tracking-widest font-medium">Admin</p>
            </div>
            {!collapsed && <div className="h-px bg-slate-700/50 mx-2 mb-1" />}
            {ADMIN_ITEMS.map((item) => (
              <SidebarLink key={item.key} item={item} collapsed={collapsed} />
            ))}
          </>
        )}
        {user?.role === "SUPER_ADMIN" && (
          <>
            <div className={`px-2 pt-3 pb-1 ${collapsed ? "hidden" : ""}`}>
              <p className="text-slate-600 text-[10px] uppercase tracking-widest font-medium">Super Admin</p>
            </div>
            {!collapsed && <div className="h-px bg-slate-700/50 mx-2 mb-1" />}
            <SidebarLink
              item={{ key: "super", label: "Panel Super Admin", icon: Shield, path: "/admin/super" }}
              collapsed={collapsed}
            />
          </>
        )}
      </nav>

      {/* User footer */}
      <div className="border-t border-slate-700/50 p-2">
        {!collapsed ? (
          <div className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-800 transition-colors group">
            <div className="w-7 h-7 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-200 text-xs font-medium truncate">{user?.name}</p>
              <p className="text-slate-500 text-[10px] truncate">{user?.role}</p>
            </div>
            <button
              data-testid={TEST_IDS.LOGOUT_BUTTON}
              onClick={handleLogout}
              className="text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
              title="Cerrar sesión"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            data-testid={TEST_IDS.LOGOUT_BUTTON}
            onClick={handleLogout}
            className="w-full flex items-center justify-center p-2 text-slate-600 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </aside>
  );
}

function SidebarLink({ item, collapsed, badge }) {
  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-all duration-150 group relative ${
          isActive
            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
            : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
        }`
      }
      title={collapsed ? item.label : undefined}
    >
      <item.icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span className="truncate font-medium">{item.label}</span>}
      {badge && (
        <span
          data-testid={TEST_IDS.UNREAD_BADGE}
          className={`ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 ${collapsed ? "absolute top-0.5 right-0.5" : ""}`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}
